import React, { useState } from 'react';
import { Plus, Edit, Trash2, Megaphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const CampaignsManagement = () => {
  const [campaigns, setCampaigns] = useState([
    {
      id: 'c1',
      name: 'Buen Fin 2025',
      description: 'Descuentos especiales durante el Buen Fin',
      startDate: '2025-11-15',
      endDate: '2025-11-18',
      isActive: true,
      stores: ['Nike', 'Zara', 'Apple Store']
    },
    {
      id: 'c2',
      name: 'Black Friday',
      description: 'Ofertas exclusivas de Black Friday',
      startDate: '2025-11-29',
      endDate: '2025-11-29',
      isActive: false,
      stores: ['Todas']
    }
  ]);

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
    });
  };

  const handleSave = () => {
    if (selectedCampaign) {
      setCampaigns(campaigns.map(c => 
        c.id === selectedCampaign.id ? { ...c, ...formData } : c
      ));
    } else {
      const newCampaign = {
        id: 'c' + (campaigns.length + 1),
        ...formData,
        isActive: false,
        stores: []
      };
      setCampaigns([...campaigns, newCampaign]);
    }
    setSelectedCampaign(null);
    setIsAdding(false);
    toast({
      title: "Campaña guardada",
      description: "La campaña ha sido actualizada correctamente",
    });
  };

  const handleDelete = (campaignId) => {
    setCampaigns(campaigns.filter(c => c.id !== campaignId));
    toast({
      title: "Campaña eliminada",
      description: "La campaña ha sido eliminada",
    });
  };

  const toggleActive = (campaignId) => {
    setCampaigns(campaigns.map(c => 
      c.id === campaignId ? { ...c, isActive: !c.isActive } : c
    ));
    toast({
      title: "Estado actualizado",
      description: "El estado de la campaña ha sido modificado",
    });
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
    });
    setIsAdding(true);
  };

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Campañas especiales</h2>
              <p className="text-[#A2A2A2]">Crea campañas temporales como Buen Fin, Black Friday, etc.</p>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-white text-black hover:bg-white/90 rounded-full px-6 py-3 transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva campaña
            </Button>
          </div>

          {/* Campaigns List */}
          <div className="space-y-3">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/20 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white">{campaign.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          campaign.isActive
                            ? 'bg-white text-black'
                            : 'bg-white/10 text-[#A2A2A2]'
                        }`}>
                          {campaign.isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                      <p className="text-[#A2A2A2] mb-2">{campaign.description}</p>
                      <div className="flex gap-4 text-sm text-[#A2A2A2]">
                        <span>Inicio: {campaign.startDate}</span>
                        <span>Fin: {campaign.endDate}</span>
                      </div>
                      {campaign.stores.length > 0 && (
                        <p className="text-sm text-white/60 mt-2">
                          Tiendas: {campaign.stores.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => toggleActive(campaign.id)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
                    >
                      {campaign.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      onClick={() => handleEdit(campaign)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(campaign.id)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-red-500/10 rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={!!selectedCampaign || isAdding} onOpenChange={() => { setSelectedCampaign(null); setIsAdding(false); }}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedCampaign ? 'Editar campaña' : 'Nueva campaña'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-white">Nombre de la campaña</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                placeholder="Ej: Buen Fin 2025"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl min-h-[80px]"
                placeholder="Describe la campaña..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Fecha inicio</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Fecha fin</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                />
              </div>
            </div>
            <Button
              onClick={handleSave}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              {selectedCampaign ? 'Guardar cambios' : 'Crear campaña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default CampaignsManagement;
